import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchUsersSummary, fetchUsersAnalytics } from "./store";
import { DeviceDistributionChart, AgeGenderDistributionChart, RegistrationTrendsChart } from './Charts';
import { ComponentLoader } from '../common/Loader';
import "./AllUsers.css";

const AllUsersStats = ({ filters, startDate, endDate }) => {
    const dispatch = useDispatch();
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [trendView, setTrendView] = useState('Monthly');

    const lastFetchParams = React.useRef(null);
    const isFetching = React.useRef(false);

    useEffect(() => {
        const currentParams = JSON.stringify({ filters, startDate, endDate });
        const cacheKey = `ALL_USERS_STATS_CACHE_${currentParams}`;
        let cacheExists = false;
        
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
            const data = JSON.parse(cachedData);
            if (data === 'PENDING') {
                return;
            }

            if (data && typeof data === 'object') {
                const cacheTimestamp = data.cacheTimestamp || 0;
                const threeMinutes = 3 * 60 * 1000;
                const isExpired = Date.now() - cacheTimestamp > threeMinutes;
                if (!isExpired) {
                    setStats(data.stats);
                    setAnalytics(data.analytics);
                    setLoadingStats(false);
                    setLoadingAnalytics(false);
                    lastFetchParams.current = currentParams;
                    return;
                } else {
                    setStats(data.stats);
                    setAnalytics(data.analytics);
                    cacheExists = true;
                } 
            }
        }

        if (lastFetchParams.current === currentParams && !cacheExists) return;
        if (isFetching.current) return;

        const loadStats = async () => {
            isFetching.current = true;
            if (!cacheExists) {
                setLoadingStats(true);
                setLoadingAnalytics(true);
            }
            sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));
        
            try {
                const [summaryRes, analyticsRes] = await Promise.all([
                    dispatch(fetchUsersSummary({ ...filters, startDate, endDate })),
                    dispatch(fetchUsersAnalytics({ ...filters, startDate, endDate }))
                ]);

                const freshStats = summaryRes.payload?.success ? summaryRes.payload : null;
                const freshAnalytics = analyticsRes.payload?.success ? analyticsRes.payload : null;

                if (freshStats) setStats(freshStats);
                if (freshAnalytics) setAnalytics(freshAnalytics);

                if (freshStats || freshAnalytics) {
                    sessionStorage.setItem(cacheKey, JSON.stringify({ 
                        stats: freshStats, 
                        analytics: freshAnalytics,
                        cacheTimestamp: Date.now() 
                    }));
                } else {
                    sessionStorage.removeItem(cacheKey);
                }
                
                lastFetchParams.current = currentParams;
            } catch (error) {
                console.error("Error fetching stats:", error);
                sessionStorage.removeItem(cacheKey);
            } finally {
                setLoadingStats(false);
                setLoadingAnalytics(false);
                isFetching.current = false;
            }
        };

        loadStats();
    }, [dispatch, filters, startDate, endDate]);

    const deviceStats = React.useMemo(() => {
        if (!analytics?.deviceDistribution) return { ios: 0, android: 0, iosPercent: 50, androidPercent: 50 };
        const ios = analytics.deviceDistribution.filter(d => d._id === 'I' || d._id === 'iOS').reduce((acc, curr) => acc + curr.count, 0);
        const android = analytics.deviceDistribution.filter(d => d._id === 'A' || d._id === 'Android').reduce((acc, curr) => acc + curr.count, 0);
        const total = ios + android || 1;
        return {
            ios, android,
            iosPercent: (ios / total) * 100,
            androidPercent: (android / total) * 100,
            iosLabel: Math.round((ios / total) * 100),
            androidLabel: Math.round((android / total) * 100)
        };
    }, [analytics]);

    const ageGenderData = React.useMemo(() => {
        const ranges = ["0-18", "19-25", "26-35", "36-45", "46-60", "Above 60"];
        if (!analytics?.ageGenderDistribution) return ranges.map(r => ({ label: r, male: 0, female: 0, others: 0, malePercent: 0, femalePercent: 0, othersPercent: 0 }));

        const raw = analytics.ageGenderDistribution;
        const bucketData = ranges.map(range => {
            const male = raw.filter(r => r._id?.range === range && String(r._id?.gender).toLowerCase() === 'male').reduce((acc, c) => acc + (c.count || 0), 0);
            const female = raw.filter(r => r._id?.range === range && String(r._id?.gender).toLowerCase() === 'female').reduce((acc, c) => acc + (c.count || 0), 0);
            const others = raw.filter(r => r._id?.range === range && !['male', 'female'].includes(String(r._id?.gender).toLowerCase())).reduce((acc, c) => acc + (c.count || 0), 0);
            return { range, male, female, others };
        });

        const maxVal = Math.max(...bucketData.flatMap(d => [d.male, d.female, d.others]), 1);

        return bucketData.map(({ range, male, female, others }) => ({
            label: range,
            male, female, others,
            malePercent: (male / maxVal) * 100,
            femalePercent: (female / maxVal) * 100,
            othersPercent: (others / maxVal) * 100
        }));
    }, [analytics]);

    const trendChartData = React.useMemo(() => {
        if (!analytics?.registrationTrends) return [];
        const data = trendView === 'Monthly' ? analytics.registrationTrends.monthly :
                     trendView === 'Weekly' ? analytics.registrationTrends.weekly :
                     analytics.registrationTrends.daily;

        if (!data || data.length === 0) return [];

        const results = [];
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        if (trendView === 'Monthly') {
            data.slice(-12).forEach(d => {
                const [year, month] = d._id.split('-');
                results.push({ label: `${monthNames[parseInt(month) - 1]}-${year.slice(-2)}`, count: d.count });
            });
        } else if (trendView === 'Daily') {
            data.slice(-15).forEach(d => {
                const [y, m, day] = d._id.split('-');
                results.push({ label: `${day}/${m}`, count: d.count });
            });
        } else {
            // Weekly
            for (let i = 0; i < Math.min(data.length, 12); i++) {
                const d = data[data.length - 1 - i];
                const weekStart = new Date(d._id);
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                const dateKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
                const match = analytics.registrationTrends.weekly?.find(w => w._id === dateKey);
                const startM = monthNames[weekStart.getMonth()];
                const endM = monthNames[weekEnd.getMonth()];
                const label = startM === endM ? `${startM}(${weekStart.getDate()}-${weekEnd.getDate()})` : `${startM}(${weekStart.getDate()})-${endM}(${weekEnd.getDate()})`;
                results.push({ label, count: match ? match.count : 0 });
            }
        }
        return results;
    }, [analytics, trendView]);

    return (
        <div className="all-users-stats-wrapper">
            <div className="row custom-mb-06">
                <div className="col-xl col-md-4 col-sm-6 mb-3">
                    <div className="stat-card-modern" style={{ borderTop: '4px solid #1e40af' }}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                           <div className="text-start">
                                <span className="stat-card-label">Total Users</span>
                                <h3 className="stat-card-value" style={{ color: '#1e293b' }}>
                                    {!loadingStats && stats ? stats.totalUsers?.toLocaleString() : <ComponentLoader color="#1e40af" size="24px" />}
                                </h3>
                           </div>
                           <div style={{ fontSize: '24px', opacity: 0.8 }}>👥</div>
                        </div>
                    </div>
                </div>
                <div className="col-xl col-md-4 col-sm-6 mb-3">
                    <div className="stat-card-modern" style={{ borderTop: '4px solid #059669' }}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                           <div className="text-start">
                                <span className="stat-card-label">Active Logins</span>
                                <h3 className="stat-card-value" style={{ color: '#059669' }}>
                                    {!loadingStats && stats ? stats.activeUsers?.toLocaleString() : <ComponentLoader color="#059669" size="24px" />}
                                </h3>
                           </div>
                           <div style={{ fontSize: '24px', opacity: 0.8 }}>✅</div>
                        </div>
                    </div>
                </div>
                <div className="col-xl col-md-4 col-sm-6 mb-3">
                    <div className="stat-card-modern" style={{ borderTop: '4px solid #dc2626' }}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                           <div className="text-start">
                                <span className="stat-card-label">Inactive Logins</span>
                                <h3 className="stat-card-value" style={{ color: '#dc2626' }}>
                                    {!loadingStats && stats ? stats.inactiveUsers?.toLocaleString() : <ComponentLoader color="#dc2626" size="24px" />}
                                </h3>
                           </div>
                           <div style={{ fontSize: '24px', opacity: 0.8 }}>🚫</div>
                        </div>
                    </div>
                </div>
                <div className="col-xl col-md-6 col-sm-6 mb-3">
                    <div className="stat-card-modern" style={{ borderTop: '4px solid #f59e0b' }}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                           <div className="text-start">
                                <span className="stat-card-label">Incomplete Profiles</span>
                                <h3 className="stat-card-value" style={{ color: '#b45309' }}>
                                    {!loadingStats && stats ? stats.incompleteUsers?.toLocaleString() : <ComponentLoader color="#f59e0b" size="24px" />}
                                </h3>
                           </div>
                           <div style={{ fontSize: '24px', opacity: 0.8 }}>⚠️</div>
                        </div>
                    </div>
                </div>
                <div className="col-xl col-md-6 col-sm-6 mb-3">
                    <div className="stat-card-modern" style={{ borderTop: '4px solid #7c3aed' }}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                           <div className="text-start">
                                <span className="stat-card-label">Today Registrations</span>
                                <h3 className="stat-card-value" style={{ color: '#7c3aed' }}>
                                    {!loadingStats && stats ? stats.todayRegistrations?.toLocaleString() : <ComponentLoader color="#7c3aed" size="24px" />}
                                </h3>
                           </div>
                           <div style={{ fontSize: '24px', opacity: 0.8 }}>✨</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="all-users-charts-wrapper">
                <div className="device-users-chart">
                    <DeviceDistributionChart deviceStats={deviceStats} />
                </div>
                <div className="age-gender-users-chart">
                    <AgeGenderDistributionChart ageGenderData={ageGenderData} />
                </div>
                <div className="registration-trends-chart">
                    <RegistrationTrendsChart 
                        trendChartData={trendChartData} 
                        trendView={trendView} 
                        setTrendView={setTrendView} 
                    />
                </div>
            </div>
        </div>
    );
};

export default AllUsersStats;
